/**
 * Teaser Carousel - Multiple instance carousel for products and collections
 * Supports 4 or 6 items per view with responsive breakpoints
 */

class TeaserCarousel {
  constructor(element, options = {}) {
    this.element = element;
    this.sectionId = element.dataset.teaserCarousel;
    this.slider = element.querySelector('.teaser-carousel__slider');
    this.items = element.querySelectorAll('.teaser-carousel__item');
    this.prevBtn = element.querySelector('.teaser-carousel__nav--prev');
    this.nextBtn = element.querySelector('.teaser-carousel__nav--next');
    this.dotsContainer = element.querySelector('.teaser-carousel__dots');
    
    // Default options
    this.options = {
      itemsPerView: 4,
      autoplay: false,
      autoplaySpeed: 3000,
      showNavigation: true,
      showDots: true,
      ...options
    };

    this.currentIndex = parseInt(element.dataset.initialSlideIndex);
    this.totalItems = this.items.length;
    this.itemsPerView = this.getItemsPerView();
    this.maxIndex = Math.max(0, this.totalItems - this.itemsPerView);
    this.autoplayInterval = null;
    
    this.init();
  }
  
  init() {
    this.setupResponsive();
    this.setupNavigation();
    this.setupDots();
    this.setupAutoplay();
    this.updateSlider();
    
    // Handle window resize
    window.addEventListener('resize', this.debounce(() => {
      this.itemsPerView = this.getItemsPerView();
      this.maxIndex = Math.max(0, this.totalItems - this.itemsPerView);
      this.currentIndex = Math.min(this.currentIndex, this.maxIndex);
      this.element.style.setProperty('--items-per-view', this.itemsPerView);
      this.updateSlider();
      this.updateDots();
    }, 250));
  }
  
  getItemsPerView() {
    const width = window.innerWidth;
    const baseItems = parseInt(this.options.itemsPerView);
    
    if (width <= 360) return 1;
    if (width <= 480) return 1;
    if (width <= 780) return 2;
    if (width <= 987) return 3;
    if (width <= 1200) return baseItems === 6 ? 4 : 3;
    
    return baseItems;
  }
  
  setupResponsive() {
    // Add responsive class to slider
    this.slider.classList.add(`teaser-carousel__slider--${this.options.itemsPerView}-items`);
    
    // Set CSS custom property for items per view
    this.element.style.setProperty('--items-per-view', this.itemsPerView);
  }
  
  setupNavigation() {
    if (!this.prevBtn || !this.nextBtn) return;
    
    this.prevBtn.addEventListener('click', () => this.prev());
    this.nextBtn.addEventListener('click', () => this.next());
    
    // Handle keyboard navigation
    this.element.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        this.prev();
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        this.next();
      }
    });
    
    // Make carousel focusable for keyboard navigation
    this.element.setAttribute('tabindex', '0');
  }
  
  setupDots() {
    if (!this.dotsContainer || !this.options.showDots) return;
    
    const totalDots = this.maxIndex + 1;
    
    for (let i = 0; i < totalDots; i++) {
      const dot = document.createElement('button');
      dot.className = 'teaser-carousel__dot';
      if (i === 0) dot.classList.add('active');
      dot.setAttribute('aria-label', `Go to slide ${i + 1}`);
      dot.addEventListener('click', () => this.goToSlide(i));
      this.dotsContainer.appendChild(dot);
    }
  }
  
  setupAutoplay() {
    if (!this.options.autoplay || this.maxIndex === 0) return;
    
    this.startAutoplay();
    
    // Pause on hover
    this.element.addEventListener('mouseenter', () => this.stopAutoplay());
    this.element.addEventListener('mouseleave', () => {
      if (this.options.autoplay) this.startAutoplay();
    });
    
    // Pause on focus
    this.element.addEventListener('focusin', () => this.stopAutoplay());
    this.element.addEventListener('focusout', () => {
      if (this.options.autoplay) this.startAutoplay();
    });
  }
  
  startAutoplay() {
    if (this.autoplayInterval || !this.options.autoplay) return;
    
    this.autoplayInterval = setInterval(() => {
      if (this.currentIndex >= this.maxIndex) {
        this.currentIndex = 0;
      } else {
        this.currentIndex++;
      }
      this.updateSlider();
      this.updateDots();
    }, this.options.autoplaySpeed);
  }
  
  stopAutoplay() {
    if (this.autoplayInterval) {
      clearInterval(this.autoplayInterval);
      this.autoplayInterval = null;
    }
  }
  
  prev() {
    if (this.currentIndex > 0) {
      this.currentIndex--;
    } else {
      this.currentIndex = this.maxIndex;
    }
    this.updateSlider();
    this.updateDots();
    this.stopAutoplay();
    if (this.options.autoplay) this.startAutoplay();
  }
  
  next() {
    if (this.currentIndex < this.maxIndex) {
      this.currentIndex++;
    } else {
      this.currentIndex = 0;
    }
    this.updateSlider();
    this.updateDots();
    this.stopAutoplay();
    if (this.options.autoplay) this.startAutoplay();
  }
  
  goToSlide(index) {
    this.currentIndex = Math.max(0, Math.min(index, this.maxIndex));
    this.updateSlider();
    this.updateDots();
    this.stopAutoplay();
    if (this.options.autoplay) this.startAutoplay();
  }
  
  updateSlider() {
    // Calculate the width of each item (100% / items per view)
    const itemWidth = 100 / this.itemsPerView;
    const translateX = -(this.currentIndex * itemWidth);
    this.slider.style.transform = `translateX(${translateX}%)`;
    
    // Update navigation button states
    if (this.prevBtn) {
      this.prevBtn.disabled = this.maxIndex === 0;
    }
    if (this.nextBtn) {
      this.nextBtn.disabled = this.maxIndex === 0;
    }
  }
  
  updateDots() {
    if (!this.dotsContainer) return;
    
    const dots = this.dotsContainer.querySelectorAll('.teaser-carousel__dot');
    dots.forEach((dot, index) => {
      dot.classList.toggle('active', index === this.currentIndex);
    });
  }
  
  destroy() {
    this.stopAutoplay();
    window.removeEventListener('resize', this.debounce);
    
    if (this.prevBtn) {
      this.prevBtn.removeEventListener('click', this.prev);
    }
    if (this.nextBtn) {
      this.nextBtn.removeEventListener('click', this.next);
    }
    
    if (this.dotsContainer) {
      const dots = this.dotsContainer.querySelectorAll('.teaser-carousel__dot');
      dots.forEach(dot => dot.removeEventListener('click', this.goToSlide));
    }
  }
  
  // Utility function for debouncing
  debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }
}

// Initialize all teaser carousels on the page
document.addEventListener('DOMContentLoaded', function() {
  const carouselElements = document.querySelectorAll('[data-teaser-carousel]');
  
  carouselElements.forEach(element => {
    // Get options from data attributes or section settings
    const sectionId = element.dataset.teaserCarousel;
    const section = document.getElementById(`s--${sectionId}`);
    
    if (section) {
      // Extract options from section (these would be set by the Liquid template)
      const itemsPerView = section.dataset.itemsPerView || 4;
      const autoplay = section.dataset.autoplay === 'true';
      const autoplaySpeed = parseInt(section.dataset.autoplaySpeed) || 3000;
      const showNavigation = section.dataset.showNavigation !== 'false';
      const showDots = section.dataset.showDots !== 'false';
      
      new TeaserCarousel(element, {
        itemsPerView: parseInt(itemsPerView),
        autoplay: autoplay,
        autoplaySpeed: autoplaySpeed,
        showNavigation: showNavigation,
        showDots: showDots
      });
    }
  });
});

// Export for potential use in other scripts
window.TeaserCarousel = TeaserCarousel;
